import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Header, Footer } from '../../components';
import { AppleEmoji } from '../../components/AppleEmoji';
import { orderApi } from '../../services/api';
import type { PaymentPageData } from '../../services/api';
import './PaymentPage.css';

type StageKey = 'paid' | 'processing' | 'completed';

const STAGE_ORDER: StageKey[] = ['paid', 'processing', 'completed'];

function computeStageIndex(status: string): number {
  switch (status) {
    case 'paid':
      return 0;
    case 'processing':
      return 1;
    case 'completed':
      return 2;
    default:
      return -1;
  }
}

function isMoscowNight(d: Date): boolean {
  // MSK = UTC+3. 23:00–09:00 MSK
  const mskHour = (d.getUTCHours() + 3) % 24;
  return mskHour >= 23 || mskHour < 9;
}

export function PaymentPage() {
  const { t } = useTranslation();
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<PaymentPageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setError('Заказ не найден');
        setIsLoading(false);
        return;
      }

      try {
        const response = await orderApi.getOrderPaymentPage(orderId);
        setOrder(response.data);
        setError(null);
      } catch (error) {
        console.error('Error fetching order:', error);
        setError('Не удалось загрузить заказ');
        setOrder(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  useEffect(() => {
    if (!order?.expires_at) {
      setTimeLeft('');
      setIsExpired(false);
      return;
    }

    // Backend returns naive UTC datetimes (no "Z"/offset suffix).
    // JS would otherwise parse them as local time — treat as UTC.
    const raw = order.expires_at;
    const needsUtcHint = !/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw);
    const expiresAt = new Date(needsUtcHint ? raw + 'Z' : raw).getTime();

    const updateTimer = () => {
      const diff = expiresAt - Date.now();

      if (diff <= 0) {
        setTimeLeft('00:00');
        setIsExpired(true);
        return;
      }

      setIsExpired(false);
      const totalSeconds = Math.floor(diff / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const mm = minutes.toString().padStart(2, '0');
      const ss = seconds.toString().padStart(2, '0');
      setTimeLeft(hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [order?.expires_at]);

  useEffect(() => {
    if (!orderId || !order) return;
    if (
      order.status === 'completed' ||
      order.status === 'cancelled' ||
      order.status === 'failed' ||
      order.status === 'expired'
    ) {
      return;
    }

    // Poll faster when we're past payment — each second counts.
    const intervalMs = order.status === 'paid' || order.status === 'processing' ? 3000 : 5000;

    const checkStatus = async () => {
      try {
        const response = await orderApi.checkOrderStatus(orderId);
        const payload = response.data;
        setOrder((prev) =>
          prev
            ? {
                ...prev,
                status: payload.status,
                tx_hash: payload.tx_hash ?? prev.tx_hash,
                paid_at: payload.paid_at ?? prev.paid_at,
                processing_at: payload.processing_at ?? prev.processing_at,
                completed_at: payload.completed_at ?? prev.completed_at,
              }
            : null
        );
      } catch (error) {
        console.error('Error checking status:', error);
      }
    };

    const interval = setInterval(checkStatus, intervalMs);
    return () => clearInterval(interval);
  }, [orderId, order?.status]);

  const formatPrice = (value: number): string => {
    return value.toLocaleString('ru-RU') + ' ₽';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const stageIndex = useMemo(
    () => (order ? computeStageIndex(order.status) : -1),
    [order?.status]
  );

  const showStages = stageIndex >= 0;
  const isCompleted = order?.status === 'completed';
  const showNightHint = useMemo(
    () => showStages && !isCompleted && isMoscowNight(new Date()),
    [showStages, isCompleted]
  );

  if (isLoading) {
    return (
      <div className="payment-page">
        <Header />
        <main className="payment-page__main">
          <div className="payment-page__loading">
            <div className="payment-page__spinner" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!order || error) {
    return (
      <div className="payment-page">
        <Header />
        <main className="payment-page__main">
          <div className="payment-page__error">
            {error || t('common.error')}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const hasQrCode = !!(order.qr_image || order.qr_code_data);

  const waitingStatus =
    order.status === 'pending' || order.status === 'waiting_payment';
  const backendExpired =
    order.status === 'expired' || order.status === 'cancelled' || order.status === 'failed';

  if ((isExpired && waitingStatus) || backendExpired) {
    return (
      <div className="payment-page">
        <Header />
        <main className="payment-page__main">
          <div className="payment-card payment-card--expired">
            <div className="payment-card__expired-icon">
              <AppleEmoji emoji="⏰" size={56} />
            </div>
            <h1 className="payment-card__title">
              {order.status === 'cancelled'
                ? 'Заказ отменён'
                : order.status === 'failed'
                ? 'Платёж не прошёл'
                : 'Время оплаты истекло'}
            </h1>
            <p className="payment-card__subtitle">
              Этот счёт больше не действителен. Создайте новый заказ, чтобы продолжить покупку.
            </p>

            <div className="payment-card__amount-hero payment-card__amount-hero--compact">
              <div className="payment-card__amount-label">{t('payment.amount')}</div>
              <div className="payment-card__amount-value">{formatPrice(order.amount_rub)}</div>
              <div className="payment-card__amount-usdt">
                <img src="/usdt.svg" alt="" width="20" height="20" style={{ borderRadius: '50%' }} />
                <span>{order.usdt_amount} USDT</span>
              </div>
            </div>

            <a href="/" className="payment-card__pay-btn">
              Создать новый заказ
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (showStages) {
    return (
      <div className="payment-page">
        <Header />
        <main className="payment-page__main">
          <div className="payment-card payment-card--stages">
            <div className="payment-card__header">
              <h1 className="payment-card__title">
                {isCompleted && (
                  <span className="payment-card__title-check">
                    <AppleEmoji emoji="✓" size={24} />
                  </span>
                )}
                {isCompleted ? t('payment.completedTitle') : t('payment.stageHeader')}
              </h1>
              <p className="payment-card__subtitle">
                {isCompleted ? t('payment.completedSubtitle') : t('payment.stageSubtitle')}
              </p>
            </div>

            <div className="payment-card__amount-hero payment-card__amount-hero--compact">
              <div className="payment-card__amount-label">{t('payment.amount')}</div>
              <div className="payment-card__amount-value">{formatPrice(order.amount_rub)}</div>
              <div className="payment-card__amount-usdt">
                <img
                  src="/usdt.svg"
                  alt=""
                  width="20"
                  height="20"
                  style={{ borderRadius: '50%' }}
                />
                <span>{order.usdt_amount} USDT</span>
              </div>
            </div>

            <ol className="stage-tracker">
              {STAGE_ORDER.map((stage, idx) => {
                const done = isCompleted ? true : idx < stageIndex;
                const active = !isCompleted && idx === stageIndex;
                const stateClass = done
                  ? 'stage-tracker__item--done'
                  : active
                  ? 'stage-tracker__item--active'
                  : 'stage-tracker__item--pending';

                return (
                  <li key={stage} className={`stage-tracker__item ${stateClass}`}>
                    <div className="stage-tracker__indicator">
                      {done ? (
                        <span className="stage-tracker__check">✓</span>
                      ) : active ? (
                        <span className="stage-tracker__pulse" />
                      ) : (
                        <span className="stage-tracker__dot" />
                      )}
                    </div>
                    <div className="stage-tracker__text">
                      <div className="stage-tracker__title">
                        {t(`payment.stages.${stage}`)}
                      </div>
                      <div className="stage-tracker__desc">
                        {t(`payment.stages.${stage}_desc`)}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>

            <div className="payment-card__info-block">
              <div className="payment-card__info-row">
                <span className="payment-card__label">{t('payment.wallet')}</span>
                <span
                  className="payment-card__value payment-card__value--mono"
                  title={order.wallet_address}
                >
                  {order.wallet_address.slice(0, 8)}…{order.wallet_address.slice(-6)}
                </span>
              </div>
              <div className="payment-card__info-row">
                <span className="payment-card__label">{t('payment.network')}</span>
                <span className="payment-card__value">
                  <span className="payment-card__network-dot" />
                  {order.wallet_network}
                </span>
              </div>
              <div className="payment-card__info-row">
                <span className="payment-card__label">{t('payment.txHashLabel')}</span>
                {order.tx_hash ? (
                  <a
                    className="payment-card__value payment-card__value--mono payment-card__tx-link"
                    href={`https://tronscan.org/#/transaction/${order.tx_hash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {order.tx_hash.slice(0, 8)}…{order.tx_hash.slice(-6)} ↗
                  </a>
                ) : (
                  <span className="payment-card__value payment-card__value--muted">
                    {t('payment.txHashPending')}
                  </span>
                )}
              </div>
            </div>

            {!isCompleted && (
              <div className="payment-card__hints">
                <div className="payment-card__eta">⏱ {t('payment.etaHint')}</div>
                {showNightHint && (
                  <div className="payment-card__night-hint">🌙 {t('payment.nightHint')}</div>
                )}
              </div>
            )}

            {isCompleted && order.tx_hash && (
              <a
                className="payment-card__pay-btn payment-card__pay-btn--success"
                href={`https://tronscan.org/#/transaction/${order.tx_hash}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('payment.txHashView')} ↗
              </a>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="payment-page">
      <Header />

      <main className="payment-page__main">
        <div className="payment-card">
          {/* Header */}
          <div className="payment-card__header">
            <h1 className="payment-card__title">
              {t('payment.title')}
            </h1>
            <p className="payment-card__subtitle">
              {hasQrCode ? t('payment.subtitle') : t('payment.waiting_payment')}
            </p>
          </div>

          {/* Amount hero */}
          <div className="payment-card__amount-hero">
            <div className="payment-card__amount-label">{t('payment.amount')}</div>
            <div className="payment-card__amount-value">{formatPrice(order.amount_rub)}</div>
            <div className="payment-card__amount-usdt">
              <img
                src="/usdt.svg"
                alt=""
                width="20"
                height="20"
                style={{ borderRadius: '50%' }}
              />
              <span>{order.usdt_amount} USDT</span>
            </div>
          </div>

          {/* Two-column body: info left, QR right */}
          <div className={`payment-card__body ${hasQrCode ? 'payment-card__body--with-qr' : ''}`}>
            <div className="payment-card__col payment-card__col--info">
              <div className="payment-card__info-block">
                <div className="payment-card__info-row">
                  <span className="payment-card__label">{t('payment.wallet')}</span>
                  <span className="payment-card__value payment-card__value--mono" title={order.wallet_address}>
                    {order.wallet_address.slice(0, 8)}…{order.wallet_address.slice(-6)}
                  </span>
                </div>
                <div className="payment-card__info-row">
                  <span className="payment-card__label">{t('payment.network')}</span>
                  <span className="payment-card__value">
                    <span className="payment-card__network-dot" />
                    {order.wallet_network}
                  </span>
                </div>
                {order.expires_at && (
                  <div className="payment-card__info-row">
                    <span className="payment-card__label">{t('payment.validUntil')}</span>
                    <span className="payment-card__value payment-card__value--muted">
                      {formatDate(order.expires_at)}
                    </span>
                  </div>
                )}
              </div>

              {timeLeft && (
                <div className={`payment-card__timer ${isExpired ? 'payment-card__timer--expired' : ''}`}>
                  <div className="payment-card__timer-label">
                    {isExpired ? 'Время истекло' : 'Осталось времени'}
                  </div>
                  <div className="payment-card__timer-value">{timeLeft}</div>
                </div>
              )}
            </div>

            {hasQrCode && (
              <div className="payment-card__col payment-card__col--qr">
                <div className="payment-card__qr-wrapper">
                  {order.qr_image ? (
                    <img
                      src={order.qr_image}
                      alt="QR Code"
                      className="payment-card__qr payment-card__qr-image"
                    />
                  ) : order.qr_code_data ? (
                    <QRCodeSVG
                      value={order.qr_code_data}
                      size={220}
                      level="M"
                      bgColor="#ffffff"
                      fgColor="#0a0b10"
                      className="payment-card__qr"
                    />
                  ) : null}
                </div>
                <div className="payment-card__qr-hint">
                  {t('payment.qrHint')}
                </div>
              </div>
            )}
          </div>

          {/* Pay button */}
          <button
            className="payment-card__pay-btn"
            onClick={() => {
              if (order.payment_url) {
                window.open(order.payment_url, '_blank');
              }
            }}
          >
            {t('common.pay')}
          </button>

          <div className={`payment-card__status payment-card__status--${order.status}`}>
            {t(`payment.status.${order.status}`, { defaultValue: order.status })}
          </div>

          {hasQrCode && (
            <p className="payment-card__processing-hint">
              {t('payment.processingHint')}
            </p>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
